import { createSlice, isAnyOf, type PayloadAction } from "@reduxjs/toolkit";
import { type ReferralDetail } from "../../domain/entities/referral-backoffice";
import { initialState } from "./initial-state";
import { getReferral } from "./thunks/get-referral";
import { fetchAllReferrals } from "./thunks/fetch-all-referrals";
import { getSummary } from "./thunks/get-summary";
import { getSupervisorAmbassadors } from "./thunks/get-supervisor-ambassadors";
import { getWeeklyTrend } from "./thunks/get-weekly-trend";
import { linkReferral } from "./thunks/link-referral";
import { listLinkCandidates } from "./thunks/list-link-candidates";
import { listReferrals } from "./thunks/list-referrals";
import { listZones } from "./thunks/list-zones";
import { unlinkReferral } from "./thunks/unlink-referral";
import { updateReferralStatus } from "./thunks/update-referral-status";
import { type ReferralsBackofficeState } from "./state";

const name = "referralsBackoffice";

/**
 * Every write answers with the full detail, so the open panel and the row
 * behind it are refreshed from the response — no refetch of the list.
 */
const applyDetail = (
  state: ReferralsBackofficeState,
  detail: ReferralDetail,
) => {
  state.detail = detail;
  const index = state.referrals.findIndex(
    (r) => r.referralId === detail.referralId,
  );
  if (index >= 0)
    state.referrals[index] = { ...state.referrals[index], ...detail };
};

export const slice = createSlice({
  name,
  initialState,
  reducers: {
    closePanel: (state) => {
      state.detail = undefined;
      state.candidates = [];
      state.mutationError = undefined;
    },
    clearCandidates: (state) => {
      state.candidates = [];
    },
    clearMutationError: (state) => {
      state.mutationError = undefined;
    },
    /** Dropped when the period changes: the rows are about to be refetched and
     *  stale children would render under fresh parents. */
    clearChildren: (state) => {
      state.childrenBySupervisor = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSummary.pending, (state) => {
        state.isSummaryPending = true;
        state.isSummaryRejected = false;
      })
      .addCase(getSummary.fulfilled, (state, action) => {
        state.summary = action.payload;
        state.isSummaryPending = false;
      })
      .addCase(getSummary.rejected, (state) => {
        state.isSummaryPending = false;
        state.isSummaryRejected = true;
      })

      .addCase(getWeeklyTrend.fulfilled, (state, action) => {
        state.weeklySeries = action.payload;
      })
      .addCase(getWeeklyTrend.rejected, (state) => {
        // The chart is secondary to the KPIs: losing it hides the card rather
        // than failing the screen.
        state.weeklySeries = undefined;
      })

      .addCase(getSupervisorAmbassadors.pending, (state, action) => {
        state.pendingChildren = [
          ...state.pendingChildren,
          action.meta.arg.supervisorId,
        ];
      })
      .addCase(getSupervisorAmbassadors.fulfilled, (state, action) => {
        const { supervisorId, groups, series } = action.payload;
        state.childrenBySupervisor[supervisorId] = { groups, series };
        state.pendingChildren = state.pendingChildren.filter(
          (id) => id !== supervisorId,
        );
      })
      .addCase(getSupervisorAmbassadors.rejected, (state, action) => {
        state.pendingChildren = state.pendingChildren.filter(
          (id) => id !== action.meta.arg.supervisorId,
        );
      })

      .addCase(listZones.fulfilled, (state, action) => {
        state.zones = action.payload;
      })

      .addCase(listReferrals.pending, (state) => {
        state.isListPending = true;
        state.isListRejected = false;
      })
      .addCase(listReferrals.fulfilled, (state, action) => {
        const { items, nextToken, append } = action.payload;
        state.referrals = append ? [...state.referrals, ...items] : items;
        state.nextToken = nextToken;
        state.isListPending = false;
      })
      .addCase(listReferrals.rejected, (state) => {
        state.isListPending = false;
        state.isListRejected = true;
      })

      // The export reads its own pages; it must not disturb the displayed list,
      // so only the pending flag is kept.
      .addCase(fetchAllReferrals.pending, (state) => {
        state.isExporting = true;
      })
      .addCase(fetchAllReferrals.fulfilled, (state) => {
        state.isExporting = false;
      })
      .addCase(fetchAllReferrals.rejected, (state) => {
        state.isExporting = false;
      })

      .addCase(getReferral.pending, (state) => {
        state.isDetailPending = true;
        state.isDetailRejected = false;
      })
      .addCase(getReferral.fulfilled, (state, action) => {
        applyDetail(state, action.payload);
        state.isDetailPending = false;
      })
      .addCase(getReferral.rejected, (state) => {
        state.isDetailPending = false;
        state.isDetailRejected = true;
      })

      .addCase(listLinkCandidates.pending, (state) => {
        state.isCandidatesPending = true;
      })
      .addCase(listLinkCandidates.fulfilled, (state, action) => {
        state.candidates = action.payload;
        state.isCandidatesPending = false;
      })
      .addCase(listLinkCandidates.rejected, (state) => {
        state.candidates = [];
        state.isCandidatesPending = false;
      })

      // The three writes share one pending flag: the panel disables all of its
      // actions while any of them is in flight.
      .addMatcher(
        isAnyOf(
          linkReferral.pending,
          unlinkReferral.pending,
          updateReferralStatus.pending,
        ),
        (state) => {
          state.isMutating = true;
          state.mutationError = undefined;
        },
      )
      .addMatcher(
        isAnyOf(
          linkReferral.fulfilled,
          unlinkReferral.fulfilled,
          updateReferralStatus.fulfilled,
        ),
        (state, action: PayloadAction<ReferralDetail>) => {
          applyDetail(state, action.payload);
          state.candidates = [];
          state.isMutating = false;
        },
      )
      .addMatcher(
        isAnyOf(
          linkReferral.rejected,
          unlinkReferral.rejected,
          updateReferralStatus.rejected,
        ),
        (state, action) => {
          state.isMutating = false;
          state.mutationError = action.error.message ?? "unknown";
        },
      );
  },
});

export const actions = slice.actions;
export const reducer = slice.reducer;
